import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ShoppingCart,
  FileText,
  Clock,
  CheckCircle,
  Package,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import purchaseOrderService from '../../services/purchaseOrderService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const PurchaseOrder = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalOrders: 0,
    draftOrders: 0,
    sentOrders: 0,
    receivedOrders: 0,
    partiallyReceivedOrders: 0,
    cancelledOrders: 0,
    totalAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [ordersResponse, statsResponse] = await Promise.all([
        purchaseOrderService.getAllPurchaseOrders(),
        purchaseOrderService.getPurchaseOrderStats()
      ])
      
      if (ordersResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (ordersResponse.data || []).map(order => ({
          ...order,
          id: order._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (ordersResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No purchase order records found. Create your first purchase order!')
        }
      } else {
        toast.error(ordersResponse.message || 'Failed to load purchase orders')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading purchase order records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view purchase orders')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load purchase order records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Sent': 'pending',
      'Acknowledged': 'pending',
      'In Transit': 'warning',
      'Partially Received': 'warning',
      'Received': 'active',
      'Cancelled': 'danger',
      'Closed': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'purchaseOrderNumber',
      header: 'PO Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'supplierName',
      header: 'Supplier',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.supplierCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.supplierCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'orderDate',
      header: 'Order Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'expectedDeliveryDate',
      header: 'Expected Delivery',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'requisitionNumber',
      header: 'Requisition #',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'financialSummary.totalAmount',
      header: 'Total Amount',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(row.original.financialSummary?.totalAmount || 0, row.original.financialSummary?.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/purchase-order/form')
  }

  const handleEdit = (orderData) => {
    navigate('/procurement/purchase-order/edit', { state: { orderData } })
  }

  const handleView = (orderData) => {
    navigate('/procurement/purchase-order/view', { state: { orderData } })
  }

  const handleDelete = (orderData) => {
    setDeleteItem(orderData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting purchase order...')
      const response = await purchaseOrderService.deletePurchaseOrder(deleteItem._id)
      if (response.success) {
        toast.success('Purchase order deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete purchase order')
      }
    } catch (error) {
      console.error('Error deleting purchase order:', error)
      if (error.response?.status === 404) {
        toast.error('Purchase order not found')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Can only delete draft purchase orders')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this purchase order')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete purchase order. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Transactions', path: '/procurement?tab=transactions' },
    { label: 'Purchase Orders', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Purchase Orders</h1>
          <p>Generated and sent purchase orders</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Order
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ShoppingCart size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalOrders || 0}</div>
          <div className="metric-title">Total Orders</div>
          <div className="metric-subtitle">All purchase orders</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftOrders || 0}</div>
          <div className="metric-title">Draft</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Sent</span>
            </div>
          </div>
          <div className="metric-value">{stats.sentOrders || 0}</div>
          <div className="metric-title">Sent</div>
          <div className="metric-subtitle">With suppliers</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Received</span>
            </div>
          </div>
          <div className="metric-value">{stats.receivedOrders || 0}</div>
          <div className="metric-title">Received</div>
          <div className="metric-subtitle">Fully received</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Package size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Partial</span>
            </div>
          </div>
          <div className="metric-value">{stats.partiallyReceivedOrders || 0}</div>
          <div className="metric-title">Partially Received</div>
          <div className="metric-subtitle">Open deliveries</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalAmount || 0, 'INR')}</div>
          <div className="metric-title">Total Amount</div>
          <div className="metric-subtitle">All orders combined</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="purchase-order-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Purchase Order"
        message={`Are you sure you want to delete purchase order "${deleteItem?.purchaseOrderNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default PurchaseOrder

